import React, { useState } from 'react';
import axios from 'axios';
import { Box, Typography, Button, TextField, Grid, styled } from '@mui/material';

const API_URL = 'https://your-api-url.com'; // Update this with your actual API URL


const SectionContainer = styled(Box)({
    background: '#000000',
    borderRadius: '20px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    color: '#ffffff',

    '@media (max-width: 600px)': {
        padding: '16px',
        marginBottom: '15px',
    },

    '@media (min-width: 600px) and (max-width: 960px)': {
        padding: '24px',
    },

    '@media (min-width: 960px)': {
        padding: '30px',
    },
});

function SubmittedData({ userId, submittedData }) {
    const [editMode, setEditMode] = useState({
        aadhar: false,
        pan: false,
        bankAccount: false,
        ifsc: false,
        bankFile: false,
        aadharFile: false,
        panFile: false
    });

    const [aadhar, setAadhar] = useState(submittedData.aadhar);
    const [pan, setPan] = useState(submittedData.pan);
    const [bankAccount, setBankAccount] = useState(submittedData.bank_account);
    const [ifsc, setIfsc] = useState(submittedData.ifsc);
    const [bankFile, setBankFile] = useState(submittedData.bank_account_file);
    const [aadharFile, setAadharFile] = useState(submittedData.aadhar_file);
    const [panFile, setPanFile] = useState(submittedData.pan_file);
    const [status, setStatus] = useState(submittedData.status);

    const [error, setError] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleEditToggle = (field) => {
        setEditMode((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSave = () => {
        const formData = new FormData();

        if (aadhar !== submittedData.aadhar) {
            formData.append('aadhar', aadhar);
        }
        if (pan !== submittedData.pan) {
            formData.append('pan', pan);
        }
        if (ifsc !== submittedData.ifsc) {
            formData.append('ifsc', ifsc);
        }
        if (bankAccount !== submittedData.bank_account) {
            formData.append('bank_account', bankAccount);
        }

        if (bankFile && bankFile !== submittedData.bank_account_file) {
            formData.append('bank_account_file', bankFile);
        }

        if (aadharFile && aadharFile !== submittedData.aadhar_file) {
            formData.append('aadhar_file', aadharFile);
        }

        if (panFile && panFile !== submittedData.pan_file) {
            formData.append('pan_file', panFile);
        }

        if (formData.entries().next().done) {
            alert("No changes were made.");
            return;
        }

        updateUserVerification(userId, formData);
    };

    const updateUserVerification = async (userId, formData) => {
        try {
            setLoading(true);
            const response = await axios.patch(
                `${API_URL}/update_user_verification/${userId}/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setLoading(false);
            alert('Details Updated successfully!');
            window.location.reload();
        } catch (error) {
            setLoading(false);
            console.error('Error updating details:', error);
            setError(['Failed to update details. Please try again later.']);
        }
    };

    const handleFileChange = (field, setter) => (e) => {
        setter(e.target.files[0]);
    };

    const handleDiscard = () => {
        setAadhar(submittedData.aadhar);
        setPan(submittedData.pan);
        setBankAccount(submittedData.bank_account);
        setIfsc(submittedData.ifsc);
        setBankFile(submittedData.bank_account_file);
        setAadharFile(submittedData.aadhar_file);
        setPanFile(submittedData.pan_file);

        setEditMode({
            aadhar: false,
            pan: false,
            bankAccount: false,
            ifsc: false,
            bankFile: false,
            aadharFile: false,
            panFile: false
        });
    };

    return (
        <div>
            <SectionContainer>
                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="h6" component="h1" color="text.primary">
                        <strong>Status:</strong>
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {status === 'approved' && 'Your account has been verified and you can now create post and request.'}
                        {status === 'pending' && 'Your verification process is under progress.'}
                        {status === 'rejected' && 'Your verification has been rejected. Please resubmit details.'}
                    </Typography>
                </Box>

                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>Aadhar:</strong>{' '}
                        {editMode.aadhar ? (
                            <TextField
                                value={aadhar}
                                onChange={(e) => setAadhar(e.target.value)}
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                autoFocus
                            />
                        ) : (
                            <>
                                {aadhar || 'Not Provided'}
                                <Button
                                    sx={{
                                        marginLeft: 2,
                                        borderColor: '#1E90FF',
                                        color: '#1E90FF',
                                        '&:hover': {
                                            borderColor: '#187bcd',
                                            color: '#187bcd',
                                        },
                                    }}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleEditToggle('aadhar')}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                    </Typography>
                </Box>
                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>Aadhar File:</strong>{' '}
                        {submittedData.aadhar_file ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {submittedData.aadhar_file.name}
                                </Typography>

                                <Button
                                    sx={{ ml: 2 }}
                                    variant="outlined"
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleEditToggle('aadharFile')}
                                >
                                    {editMode.aadharFile ? 'Cancel' : 'Edit'}
                                </Button>
                                {editMode.aadharFile && (
                                    <input
                                        type="file"
                                        accept=".pdf, .jpg, .jpeg, .png"
                                        onChange={handleFileChange('aadharFile', setAadharFile)}
                                        style={{ marginTop: '1rem' }}
                                    />
                                )}
                            </>
                        ) : (
                            'No File Attached'
                        )}
                    </Typography>
                </Box>

                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>Pan:</strong>{' '}
                        {editMode.pan ? (
                            <TextField
                                value={pan}
                                onChange={(e) => setPan(e.target.value)}
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                autoFocus
                            />
                        ) : (
                            <>
                                {pan || 'Not Provided'}
                                <Button
                                    sx={{
                                        marginLeft: 2,
                                        borderColor: '#1E90FF',
                                        color: '#1E90FF',
                                        '&:hover': {
                                            borderColor: '#187bcd',
                                            color: '#187bcd',
                                        },
                                    }}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleEditToggle('pan')}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                    </Typography>
                </Box>

                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>Pan File:</strong>{' '}
                        {submittedData.pan_file ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {submittedData.pan_file.name}
                                </Typography>

                                <Button
                                    sx={{ ml: 2 }}
                                    variant="outlined"
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleEditToggle('panFile')}
                                >
                                    {editMode.panFile ? 'Cancel' : 'Edit'}
                                </Button>
                                {editMode.panFile && (
                                    <input
                                        type="file"
                                        accept=".pdf, .jpg, .jpeg, .png"
                                        onChange={handleFileChange('panFile', setPanFile)}
                                        style={{ marginTop: '1rem' }}
                                    />
                                )}
                            </>
                        ) : (
                            'No File Attached'
                        )}
                    </Typography>
                </Box>

                {/* Bank Account */}
                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>Bank Account:</strong>{' '}
                        {editMode.bankAccount ? (
                            <TextField
                                value={bankAccount}
                                onChange={(e) => setBankAccount(e.target.value)}
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                autoFocus
                            />
                        ) : (
                            <>
                                {bankAccount || 'Not Provided'}
                                <Button
                                    sx={{
                                        marginLeft: 2,
                                        borderColor: '#1E90FF',
                                        color: '#1E90FF',
                                        '&:hover': {
                                            borderColor: '#187bcd',
                                            color: '#187bcd',
                                        },
                                    }}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleEditToggle('bankAccount')}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                    </Typography>
                </Box>

                {/* IFSC */}
                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>IFSC:</strong>{' '}
                        {editMode.ifsc ? (
                            <TextField
                                value={ifsc}
                                onChange={(e) => setIfsc(e.target.value)}
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                autoFocus
                            />
                        ) : (
                            <>
                                {ifsc || 'Not Provided'}
                                <Button
                                    sx={{
                                        marginLeft: 2,
                                        borderColor: '#1E90FF',
                                        color: '#1E90FF',
                                        '&:hover': {
                                            borderColor: '#187bcd',
                                            color: '#187bcd',
                                        },
                                    }}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleEditToggle('ifsc')}
                                >
                                    Edit
                                </Button>
                            </>
                        )}
                    </Typography>
                </Box>

                {/* Bank Account File */}
                <Box sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" color="text.primary">
                        <strong>Bank Account File:</strong>{' '}
                        {submittedData.bank_account_file ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {submittedData.bank_account_file.name}
                                </Typography>

                                <Button
                                    sx={{ ml: 2 }}
                                    variant="outlined"
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleEditToggle('bankFile')}
                                >
                                    {editMode.bankFile ? 'Cancel' : 'Edit'}
                                </Button>
                                {editMode.bankFile && (
                                    <input
                                        type="file"
                                        accept=".pdf, .jpg, .jpeg, .png"
                                        onChange={handleFileChange('bankFile', setBankFile)}
                                        style={{ marginTop: '1rem' }}
                                    />
                                )}
                            </>
                        ) : (
                            'No File Attached'
                        )}
                    </Typography>
                </Box>





                {/* Save Changes Button */}
                {(editMode.aadhar || editMode.pan || editMode.bankAccount || editMode.ifsc || editMode.bankFile || editMode.aadharFile || editMode.panFile) && (
                    <Grid container justifyContent="center" sx={{ marginTop: '2rem' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                sx={{
                                    backgroundColor: '#1E90FF',
                                    '&:hover': {
                                        backgroundColor: '#187bcd',
                                    },
                                    marginRight: 2,
                                }}
                            >
                                Save Changes
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleDiscard}
                            >
                                Discard Changes
                            </Button>
                        </Box>
                    </Grid>
                )}
            </SectionContainer>
        </div>
    );
}

export default SubmittedData;
